import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import config from '../config';

const INTERVAL_ORDER = {
  year: 0,
  month: 1,
};

const STAGING_MEMBERSHIP_PRODUCT_ID = 'prod_T7Z7uvNzZXeVBd';

export const FALLBACK_MEMBERSHIP_FEATURES = [
  'Research-backed supplement stacks',
  'Medication and diet-aware recommendations',
  'Advanced personalized analysis',
];

export function formatPriceAmount(unitAmount, currency = 'usd') {
  if (!Number.isFinite(unitAmount)) {
    return '';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: unitAmount % 100 === 0 ? 0 : 2,
  }).format(unitAmount / 100);
}

export function formatMonthlyEquivalent(unitAmount, intervalCount = 1, currency = 'usd') {
  if (!Number.isFinite(unitAmount)) {
    return '';
  }

  const months = 12 * intervalCount;
  const monthlyAmount = unitAmount / months;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: monthlyAmount % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(monthlyAmount / 100);
}

function normalizeMarketingFeatures(marketingFeatures) {
  if (!Array.isArray(marketingFeatures)) {
    return [];
  }

  return marketingFeatures
    .map((feature) => {
      if (typeof feature === 'string') return feature;
      return feature?.name;
    })
    .filter(Boolean);
}

function normalizePlan(priceId, priceData, productData) {
  const interval = priceData.interval || priceData.recurring?.interval;
  const intervalCount = priceData.interval_count || priceData.recurring?.interval_count || 1;
  const currency = priceData.currency || 'usd';
  const metadata = priceData.metadata || {};
  const amountLabel = formatPriceAmount(priceData.unit_amount, currency);
  const monthlyEquivalentLabel = interval === 'year'
    ? formatMonthlyEquivalent(priceData.unit_amount, intervalCount, currency)
    : '';
  const intervalLabel = interval === 'year' ? 'year' : 'month';
  const defaultCta = interval === 'year' ? 'Start Annual Membership' : 'Start Monthly Membership';

  return {
    priceId,
    interval,
    intervalCount,
    unitAmount: priceData.unit_amount,
    currency,
    amountLabel,
    monthlyEquivalentLabel,
    intervalLabel,
    description: priceData.description || '',
    productName: productData.name || 'myStack Membership',
    productDescription: productData.description || '',
    metadata,
    displayLabel: metadata.display_label || (interval === 'year' ? 'Best Value' : ''),
    ctaLabel: metadata.cta_label || defaultCta,
    // Strikethrough "was" price. Opt-in only: set original_unit_amount (in
    // cents) on the Stripe price to show one. Never infer it from archived
    // prices — an archived higher price is not necessarily a former list
    // price, and an unintended "was" figure reads as a limited-time
    // introductory discount (App Store guideline 3.1.2(c)).
    originalAmountLabel: metadata.original_unit_amount
      ? formatPriceAmount(Number(metadata.original_unit_amount), currency)
      : '',
  };
}

function getPriceInterval(priceData) {
  return priceData.interval || priceData.recurring?.interval;
}

function isDisplayablePrice(priceData) {
  const intervalCount = priceData?.interval_count || priceData?.recurring?.interval_count || 1;

  return (
    priceData?.active === true &&
    priceData?.type === 'recurring' &&
    ['month', 'year'].includes(getPriceInterval(priceData)) &&
    intervalCount === 1 &&
    Number.isFinite(priceData.unit_amount) &&
    (priceData.currency || 'usd') === 'usd'
  );
}

function pickPlanPerInterval(plans) {
  const bestByInterval = new Map();

  plans.forEach((plan) => {
    const current = bestByInterval.get(plan.interval);
    const currentRank = getPlanRank(current);
    const nextRank = getPlanRank(plan);

    if (!current || nextRank < currentRank) {
      bestByInterval.set(plan.interval, plan);
    }
  });

  return Array.from(bestByInterval.values())
    .sort((a, b) => (INTERVAL_ORDER[a.interval] ?? 99) - (INTERVAL_ORDER[b.interval] ?? 99));
}

function getPlanRank(plan) {
  if (!plan) return Number.POSITIVE_INFINITY;
  if (plan.metadata?.current === 'true' || plan.metadata?.display === 'true') return 0;
  return plan.unitAmount;
}

export async function getMembershipPricing() {
  const productId =
    config.STRIPE_MEMBERSHIP_PRODUCT_ID ||
    (process.env.REACT_APP_ENVIRONMENT !== 'production' ? STAGING_MEMBERSHIP_PRODUCT_ID : '');

  if (!productId) {
    throw new Error('Membership product is not configured.');
  }

  const productRef = doc(db, 'products', productId);
  const productSnap = await getDoc(productRef);

  if (!productSnap.exists()) {
    throw new Error('Membership product was not found.');
  }

  const productData = productSnap.data();
  const pricesSnap = await getDocs(collection(productRef, 'prices'));

  const displayablePlans = pricesSnap.docs
    .filter((priceDoc) => isDisplayablePrice(priceDoc.data()))
    .map((priceDoc) => normalizePlan(priceDoc.id, priceDoc.data(), productData));

  const plans = pickPlanPerInterval(displayablePlans);

  if (process.env.NODE_ENV === 'development') {
    console.debug('[subscriptionPricing] loaded from Firestore', {
      productId,
      productName: productData.name,
      plans: plans.map((plan) => ({
        priceId: plan.priceId,
        interval: plan.interval,
        unitAmount: plan.unitAmount,
        currency: plan.currency,
      })),
    });
  }

  return {
    productId,
    productName: productData.name || 'myStack Membership',
    productDescription: productData.description || '',
    features: normalizeMarketingFeatures(productData.marketing_features),
    plans,
  };
}
